import { useCallback, useLayoutEffect, useRef, useState, CSSProperties } from 'react';
import { format } from 'date-fns';
import { chevronLeft, clock, flashSlash, messageWhite, repair, truck } from 'assets/images';
import { TipoColorLineaAvanceEnum, TipoEstadoActualSecundarioTrackingCorteEnum, TipoVarianteDetalleEnum } from 'config';
import { useTranslation } from 'react-i18next';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import {
  ETIQUETA_A_LAS,
  FORMATO_DIA_MES_ANIO,
  FORMATO_HORA,
  formatLastUpdate,
  formatTerDisplay,
  formatTimeOnly,
  parseFlexibleDate,
  SUFIJO_HORAS,
  TIPO_FECHA_HOY,
  TIPO_FECHA_MANANA,
  TIPO_FECHA_OTRO_DIA,
  TIPO_FECHA_VACIO,
} from 'utils/formatters/dateFormatter';
import { datoNoValido } from 'utils/colorBallsUtils';
import { CADENA_VACIA } from 'utils/constants';
import { mapApiResponseToDashboardData } from 'utils/outageDataMapper';
import BotonCerrarDashboard from './BotonCerrarDashboard';
import useStyles from './DetalleAvanceStyles';
import { buildFooterTexto, buildTerLabels } from './outageDashboard.helpers';
import {
  ESPERA_REMEDIR_TIMELINE_MS,
  ESTADO_NODO_ACTIVO,
  ESTADO_NODO_HECHO,
  ESTADO_NODO_PENDIENTE,
  INDICE_BRIGADA_ASIGNADA,
  INDICE_BRIGADA_TERRENO,
  INDICE_CORTE_CONFIRMADO,
  INDICE_INICIO_PROGRAMADO,
  INDICE_REPOSICION,
  INDICE_REPOSICION_PROGRAMADO,
  ALTO_MINIMO_LINEA,
  CANTIDAD_MINIMA_NODOS_LINEA,
  MARCADOR_CUANDO,
  StepKind,
  TEST_ID_DETALLE_CUATRO,
  TEST_ID_DETALLE_DOS,
  TEST_ID_DETALLE_FUTURO,
  TEST_ID_DETALLE_PROGRAMADO,
  ID_PASO_ASIGNACION,
  ID_PASO_CORTE,
  ID_PASO_INICIO,
  ID_PASO_NOTIFICACION,
  ID_PASO_REPOSICION,
  ID_PASO_TERRENO,
  ATRIBUTO_NODO_TIMELINE,
  DIVISOR_CENTRO_NODO,
  PREFIJO_TEST_ID_PASO,
  VARIABLE_LINEA_ALTO,
  VARIABLE_LINEA_COLOR,
  VARIABLE_LINEA_TOP,
  UNIDAD_PX,
  ID_TITULO_DETALLE,
  TEST_ID_DETALLE_MODAL,
  TEST_ID_DETALLE_VOLVER,
} from './outageDashboard.constants';

/**
 * Detalle vertical de avance — Figma 8854:111395.
 * La línea se dibuja uniendo centros de nodos (medidos en layout) → sin huecos.
 */

/**
 * Reemplaza un prefijo al inicio del texto (case-insensitive), solo si hay límite de palabra.
 * @param texto texto original
 * @param prefijo prefijo a buscar
 * @param reemplazo texto de reemplazo
 * @returns texto con el prefijo sustituido o el original
 */
const reemplazarPrefijoInicio = (texto: string, prefijo: string, reemplazo: string): string => {
  if (!prefijo || texto.length < prefijo.length) {
    return texto;
  }
  const cabeza = texto.slice(0, prefijo.length);
  if (cabeza.toLocaleLowerCase() !== prefijo.toLocaleLowerCase()) {
    return texto;
  }
  const resto = texto.slice(prefijo.length);
  if (resto.length > 0) {
    const primer = resto.charAt(0);
    if (primer !== ' ' && primer !== '\t' && primer !== '\n' && primer !== '\r') {
      return texto;
    }
  }
  return `${reemplazo}${resto}`;
};

/**
 * Paso del detalle de avance vertical.
 */
type Paso = {
  id: string;
  hora: string;
  titulo: string;
  descripcion: string;
  /**
   * La reposición muestra texto también cuando el paso está pendiente.
   */
  mostrarDescSiPendiente: boolean;
  /**
   * Figma: descripción solo en el paso activo.
   * Excepción: corte confirmado mantiene el motivo al completar.
   */
  persistirDescripcion?: boolean;
  icono: string;
};

/**
 * Tramo de la línea que une dos nodos del timeline.
 */
type LineaSeg = {
  /**
   * Posición del tramo en el timeline. Distingue tramos con la misma medida.
   */
  indice: number;
  top: number;
  height: number;
  color: TipoColorLineaAvanceEnum;
};

/**
 * Arma la descripción de reposición con la hora ya formateada.
 * @param terPrincipal texto de Hoy, Mañana o fecha
 * @param kind casuística de la fecha
 * @param t traductor de i18n
 * @returns descripción de reposición o el texto de hora no informada
 */
const buildReposicionDescripcion = (
  terPrincipal: string,
  kind: typeof TIPO_FECHA_HOY | typeof TIPO_FECHA_MANANA | typeof TIPO_FECHA_OTRO_DIA | typeof TIPO_FECHA_VACIO,
  t: (key: string, opts?: Record<string, string>) => string
): string => {
  if (!terPrincipal || kind === TIPO_FECHA_VACIO) {
    return t('desconexion:paginas.mapaCortes.modalTracking.dashboard.detalle.sinHoraReposicion');
  }
  const prefijoHoy = t('desconexion:paginas.mapaCortes.modalTracking.fechas.hoy');
  const prefijoManana = t('desconexion:paginas.mapaCortes.modalTracking.fechas.manana');
  const fragmentoHoy = t('desconexion:paginas.mapaCortes.modalTracking.fechas.hoyFragmento');
  const fragmentoManana = t('desconexion:paginas.mapaCortes.modalTracking.fechas.mananaFragmento');
  const fragmento = reemplazarPrefijoInicio(
    reemplazarPrefijoInicio(terPrincipal, prefijoHoy, fragmentoHoy),
    prefijoManana,
    fragmentoManana
  );
  return t('desconexion:paginas.mapaCortes.modalTracking.dashboard.detalle.reposicionConHora', {
    hora: fragmento,
  });
};

/**
 * Vista de detalle de avance del corte imprevisto, programado o futuro.
 * @param data respuesta de tracking del corte
 * @param numeroOrden identificador de la orden
 * @param onBack vuelve al dashboard
 * @param variante tipo de corte que define etapas y colores
 * @param comuna comuna del polígono para el motivo
 */
const DetalleAvance = ({
  data,
  numeroOrden,
  onBack,
  variante = TipoVarianteDetalleEnum.IMPREVISTO,
  comuna = CADENA_VACIA,
  onCerrar = undefined,
}: {
  data: TrackingCorteEstadosResponseModel;
  numeroOrden: string;
  onBack: () => void;
  variante?: TipoVarianteDetalleEnum;
  comuna?: string;
  onCerrar?: () => void;
}) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Contenedor del timeline, para medir los centros de los nodos.
   */
  const timelineRef = useRef<HTMLDivElement>(null);

  /**
   * Tramos de la línea de avance, medidos entre los centros de las bolas.
   */
  const [lineas, setLineas] = useState<LineaSeg[]>([]);

  /**
   * Datos de vista del detalle a partir de la respuesta de tracking.
   */
  const dashboard = mapApiResponseToDashboardData(data, numeroOrden);
  /**
   * Estado secundario del corte, usado para el color del timeline.
   */
  const secundario = data.estadoActualSecundario;
  /**
   * El detalle usa las etapas del corte programado en curso.
   */
  const esProgramado = variante === TipoVarianteDetalleEnum.PROGRAMADO;
  /**
   * El detalle usa las etapas del próximo corte programado.
   */
  const esFuturo = variante === TipoVarianteDetalleEnum.FUTURO;
  /**
   * El corte concluido se pinta en verde solo si está normalizado. El futuro queda en notificación.
   */
  const esVerde = !esFuturo && secundario === TipoEstadoActualSecundarioTrackingCorteEnum.NORMALIZADO;
  const sinMotivo = datoNoValido(dashboard.motivo_corte);

  const usa4Etapas =
    !esFuturo &&
    (dashboard.hasBrigadaAsignada ||
      esVerde ||
      Boolean(data.fechaArriboBrigada) ||
      secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_ASIGNADA ||
      secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO);

  /**
   * Índice del paso activo en el timeline de cuatro etapas.
   */
  const pasoActual4: number = (() => {
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.NORMALIZADO) {
      return INDICE_REPOSICION;
    }
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_EN_TERRENO) {
      return INDICE_BRIGADA_TERRENO;
    }
    if (secundario === TipoEstadoActualSecundarioTrackingCorteEnum.BRIGADA_ASIGNADA) {
      return INDICE_BRIGADA_ASIGNADA;
    }
    return INDICE_CORTE_CONFIRMADO;
  })();

  const terLabels = buildTerLabels(t);
  const terDisplay = formatTerDisplay(data.fechaFinCorte, terLabels);

  const motivoTexto = sinMotivo
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.motivoPendiente')
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.detalle.motivoCorte', {
        motivo: dashboard.motivo_corte,
      });

  const reposicionDesc = buildReposicionDescripcion(terDisplay.principal, terDisplay.kind, t);
  const horaReposicion = formatTimeOnly(data.fechaFinCorte);
  const horaCorte = formatTimeOnly(dashboard.hora_etapa.corteConfirmado);
  const horaAsignacion = formatTimeOnly(dashboard.hora_etapa.asignacionBrigada);
  const horaTerreno = formatTimeOnly(dashboard.hora_etapa.brigadaEnTerreno);

  /**
   * En la última etapa el título es reposición; antes es reposición estimada.
   */
  const tituloReposicion = esVerde
    ? t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.reposicion')
    : t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.reposicionServicio');

  const pasos4: Paso[] = [
    {
      id: ID_PASO_CORTE,
      hora: horaCorte,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.corteConfirmado'),
      descripcion: motivoTexto,
      mostrarDescSiPendiente: false,
      persistirDescripcion: true,
      icono: flashSlash,
    },
    {
      id: ID_PASO_ASIGNACION,
      hora: horaAsignacion,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.asignacionBrigada'),
      descripcion: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.textos.asignacionBrigada'),
      mostrarDescSiPendiente: false,
      icono: truck,
    },
    {
      id: ID_PASO_TERRENO,
      hora: horaTerreno,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.brigadaEnTerreno'),
      descripcion: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.detalle.brigadaEnTerreno'),
      mostrarDescSiPendiente: false,
      icono: repair,
    },
    {
      id: ID_PASO_REPOSICION,
      hora: horaReposicion,
      titulo: tituloReposicion,
      descripcion: reposicionDesc,
      mostrarDescSiPendiente: true,
      icono: clock,
    },
  ];

  const pasos2: Paso[] = [
    {
      id: ID_PASO_CORTE,
      hora: horaCorte,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.corteConfirmado'),
      descripcion: motivoTexto,
      mostrarDescSiPendiente: false,
      persistirDescripcion: true,
      icono: flashSlash,
    },
    {
      id: ID_PASO_REPOSICION,
      hora: horaReposicion,
      titulo: tituloReposicion,
      descripcion: reposicionDesc,
      mostrarDescSiPendiente: true,
      icono: clock,
    },
  ];

  const motivoHardcoded = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.motivoHardcoded');
  const comunaTexto = (comuna || CADENA_VACIA).trim();
  const motivoYComuna = comunaTexto
    ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.motivoYComuna', {
        motivo: motivoHardcoded,
        comuna: comunaTexto,
      })
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.motivoSinComuna', {
        motivo: motivoHardcoded,
      });
  /**
   * La notificación del corte futuro muestra fecha y hora absoluta.
   */
  const horaNotificacion = formatLastUpdate(data.fechaCreacionCaso);

  const pasosProgramado: Paso[] = [
    {
      id: ID_PASO_NOTIFICACION,
      hora: horaNotificacion,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.detalleNotificacion'),
      descripcion: motivoYComuna,
      mostrarDescSiPendiente: false,
      persistirDescripcion: true,
      icono: flashSlash,
    },
    {
      id: ID_PASO_INICIO,
      hora: horaCorte,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.detalleInicio'),
      descripcion: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.estadoInicio'),
      mostrarDescSiPendiente: false,
      icono: flashSlash,
    },
    {
      id: ID_PASO_REPOSICION,
      hora: horaReposicion,
      titulo: tituloReposicion,
      descripcion: reposicionDesc,
      mostrarDescSiPendiente: true,
      icono: clock,
    },
  ];

  /**
   * Formatea fecha y hora absolutas para las etapas futuras (dd/MM/yyyy a las HH:mm).
   * @param value fecha de inicio o fin del corte
   * @returns texto con fecha y hora, o cadena vacía si no hay fecha
   */
  const fechaAbsoluta = (value?: string | null): string => {
    const fecha = parseFlexibleDate(value);
    if (!fecha) {
      return CADENA_VACIA;
    }
    return `${format(fecha, FORMATO_DIA_MES_ANIO)} ${ETIQUETA_A_LAS} ${format(fecha, FORMATO_HORA)}`;
  };
  const inicioAbsoluto = fechaAbsoluta(data.fechaInicioCorte);
  const finAbsoluto = fechaAbsoluta(data.fechaFinCorte);
  const pasosFuturo: Paso[] = [
    {
      id: ID_PASO_NOTIFICACION,
      hora: horaNotificacion,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.notificacion'),
      descripcion: motivoYComuna,
      mostrarDescSiPendiente: false,
      persistirDescripcion: true,
      icono: messageWhite,
    },
    {
      id: ID_PASO_INICIO,
      hora: CADENA_VACIA,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.programado.detalleInicio'),
      descripcion: inicioAbsoluto
        ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.detalleInicio', { cuando: MARCADOR_CUANDO }).replace(
            MARCADOR_CUANDO,
            inicioAbsoluto
          )
        : CADENA_VACIA,
      mostrarDescSiPendiente: true,
      icono: flashSlash,
    },
    {
      id: ID_PASO_REPOSICION,
      hora: CADENA_VACIA,
      titulo: t('desconexion:paginas.mapaCortes.modalTracking.textosStepper.titulos.reposicionServicio'),
      descripcion: finAbsoluto
        ? t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.detalleReposicion', { cuando: MARCADOR_CUANDO }).replace(
            MARCADOR_CUANDO,
            finAbsoluto
          )
        : CADENA_VACIA,
      mostrarDescSiPendiente: true,
      icono: clock,
    },
  ];

  let pasos = pasos2;
  if (esFuturo) {
    pasos = pasosFuturo;
  } else if (esProgramado) {
    pasos = pasosProgramado;
  } else if (usa4Etapas) {
    pasos = pasos4;
  }
  let pasoActivoIndex = pasoActual4;
  if (esFuturo) {
    pasoActivoIndex = INDICE_CORTE_CONFIRMADO;
  } else if (esProgramado) {
    pasoActivoIndex = esVerde ? INDICE_REPOSICION_PROGRAMADO : INDICE_INICIO_PROGRAMADO;
  } else if (!usa4Etapas) {
    if (esVerde) {
      pasoActivoIndex = INDICE_INICIO_PROGRAMADO;
    } else {
      pasoActivoIndex = INDICE_CORTE_CONFIRMADO;
    }
  }

  /**
   * Determina si un paso está completado, activo o pendiente.
   * @param index posición del paso en el timeline
   * @returns estado visual del nodo
   */
  const stepKind = (index: number): StepKind => {
    if (esVerde) {
      return index < pasos.length - 1 ? ESTADO_NODO_HECHO : ESTADO_NODO_ACTIVO;
    }
    if (index < pasoActivoIndex) {
      return ESTADO_NODO_HECHO;
    }
    if (index === pasoActivoIndex) {
      return ESTADO_NODO_ACTIVO;
    }
    return ESTADO_NODO_PENDIENTE;
  };

  const footerTexto = buildFooterTexto(data.fechaActualizacion, t);
  let colorProgreso = TipoColorLineaAvanceEnum.IMPREVISTO;
  if (esVerde) {
    colorProgreso = TipoColorLineaAvanceEnum.NORMALIZADO;
  } else if (esProgramado) {
    colorProgreso = TipoColorLineaAvanceEnum.PROGRAMADO;
  } else if (esFuturo) {
    colorProgreso = TipoColorLineaAvanceEnum.FUTURO;
  }

  /**
   * Mide los centros de los nodos y arma los tramos de la línea de avance.
   */
  const medirLineas = useCallback(() => {
    const root = timelineRef.current;
    if (!root) {
      return;
    }

    /**
     * Iguala la altura de los pasos intermedios para que los tramos de la línea queden parejos.
     */
    const pasosEl = Array.from(root.querySelectorAll<HTMLElement>(`[data-testid^="${PREFIJO_TEST_ID_PASO}"]`));
    if (pasosEl.length >= CANTIDAD_MINIMA_NODOS_LINEA) {
      const intermedios = pasosEl.slice(0, -1);
      for (let i = 0; i < intermedios.length; i += 1) {
        intermedios[i].style.minHeight = CADENA_VACIA;
      }
      const maxH = Math.max(...intermedios.map(pasoEl => pasoEl.getBoundingClientRect().height), ALTO_MINIMO_LINEA);
      const minH = `${Math.ceil(maxH)}px`;
      for (let i = 0; i < intermedios.length; i += 1) {
        if (intermedios[i].style.minHeight !== minH) {
          intermedios[i].style.minHeight = minH;
        }
      }
    }

    const nodos = Array.from(root.querySelectorAll<HTMLElement>(`[${ATRIBUTO_NODO_TIMELINE}]`));
    if (nodos.length < CANTIDAD_MINIMA_NODOS_LINEA) {
      setLineas([]);
      return;
    }

    const rootTop = root.getBoundingClientRect().top;
    const centros = nodos.map(nodo => {
      const r = nodo.getBoundingClientRect();
      return r.top - rootTop + r.height / DIVISOR_CENTRO_NODO;
    });

    const segs: LineaSeg[] = [];
    for (let i = 0; i < centros.length - 1; i += 1) {
      let kind: StepKind = ESTADO_NODO_PENDIENTE;
      if (esVerde) {
        kind = i < nodos.length - 1 ? ESTADO_NODO_HECHO : ESTADO_NODO_ACTIVO;
      } else if (i < pasoActivoIndex) {
        kind = ESTADO_NODO_HECHO;
      } else if (i === pasoActivoIndex) {
        kind = ESTADO_NODO_ACTIVO;
      }
      const color = kind === ESTADO_NODO_HECHO ? colorProgreso : TipoColorLineaAvanceEnum.PENDIENTE;
      const top = centros[i];
      const height = Math.max(centros[i + 1] - top, ALTO_MINIMO_LINEA);
      segs.push({ indice: i, top, height, color });
    }
    setLineas(segs);
  }, [pasoActivoIndex, esVerde, colorProgreso]);

  /**
   * Mide el timeline al montar y lo vuelve a medir tras pintar fuentes e imágenes.
   */
  useLayoutEffect(() => {
    medirLineas();
    const remedirId = window.setTimeout(medirLineas, ESPERA_REMEDIR_TIMELINE_MS);

    const root = timelineRef.current;
    let ro: ResizeObserver | null = null;
    if (root && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        medirLineas();
      });
      ro.observe(root);
    }
    window.addEventListener('resize', medirLineas);
    return () => {
      window.clearTimeout(remedirId);
      if (ro) {
        ro.disconnect();
      }
      window.removeEventListener('resize', medirLineas);
    };
  }, [medirLineas, pasos.length, usa4Etapas, esProgramado]);

  /**
   * Dibuja el nodo del paso según si está hecho, activo o pendiente.
   * @param paso datos del paso
   * @param kind estado visual del nodo
   * @returns círculo o ícono del paso
   */
  const renderNodo = (paso: Paso, kind: StepKind) => {
    /**
     * El atributo de medición va en el círculo visible para tomar el centro real.
     */
    if (kind === ESTADO_NODO_HECHO) {
      let doneExtra = CADENA_VACIA;
      if (esVerde) {
        doneExtra = styles.nodoDoneGreen;
      } else if (esProgramado) {
        doneExtra = styles.nodoDoneNaranja;
      } else if (esFuturo) {
        doneExtra = styles.nodoActiveAzul;
      }
      return <span className={`${styles.nodoDone} ${doneExtra}`} {...{ [ATRIBUTO_NODO_TIMELINE]: true }} aria-hidden />;
    }
    if (kind === ESTADO_NODO_ACTIVO) {
      let activeExtra = styles.nodoActive;
      if (esVerde) {
        activeExtra = styles.nodoActiveGreen;
      } else if (esProgramado) {
        activeExtra = styles.nodoActiveNaranja;
      } else if (esFuturo) {
        activeExtra = styles.nodoActiveAzul;
      }
      return (
        <span className={`${styles.nodoCircle} ${activeExtra}`} {...{ [ATRIBUTO_NODO_TIMELINE]: true }} aria-hidden>
          <img src={paso.icono} alt={CADENA_VACIA} className={styles.iconoNodo} />
        </span>
      );
    }
    return (
      <span className={`${styles.nodoCircle} ${styles.nodoPending}`} {...{ [ATRIBUTO_NODO_TIMELINE]: true }} aria-hidden>
        <img src={paso.icono} alt={CADENA_VACIA} className={styles.iconoNodo} />
      </span>
    );
  };

  /**
   * Arma título, hora y descripción del paso según su estado.
   * @param paso datos del paso
   * @param kind estado visual del nodo
   * @returns textos del paso
   */
  const renderTextos = (paso: Paso, kind: StepKind) => {
    if (kind === ESTADO_NODO_PENDIENTE) {
      return (
        <>
          <p className={styles.tituloPendiente}>{paso.titulo}</p>
          {paso.mostrarDescSiPendiente && <p className={styles.descripcion}>{paso.descripcion}</p>}
        </>
      );
    }

    /**
     * El paso activo siempre muestra descripción. El completado solo si el texto debe persistir.
     */
    const mostrarDesc = kind === ESTADO_NODO_ACTIVO || Boolean(paso.persistirDescripcion);

    let colorTitulo = styles.tituloRojo;
    if (esVerde) {
      colorTitulo = styles.tituloVerde;
    } else if (esProgramado) {
      colorTitulo = styles.tituloNaranja;
    } else if (esFuturo) {
      colorTitulo = styles.tituloAzul;
    }
    const tituloClass = `${styles.titulo} ${colorTitulo}`;
    return (
      <>
        {!datoNoValido(paso.hora) && (
          <p className={esFuturo && paso.id === ID_PASO_NOTIFICACION ? styles.horaFecha : styles.hora}>{`${paso.hora}${SUFIJO_HORAS}`}</p>
        )}
        <p className={tituloClass}>{paso.titulo}</p>
        {mostrarDesc && <p className={styles.descripcion}>{paso.descripcion}</p>}
      </>
    );
  };

  /**
   * Identificador de prueba del timeline según el tipo de corte.
   */
  const timelineTestId = (() => {
    if (esFuturo) {
      return TEST_ID_DETALLE_FUTURO;
    }
    if (esProgramado) {
      return TEST_ID_DETALLE_PROGRAMADO;
    }
    return usa4Etapas ? TEST_ID_DETALLE_CUATRO : TEST_ID_DETALLE_DOS;
  })();

  return (
    <div className={styles.capa} data-testid={TEST_ID_DETALLE_MODAL} role="dialog" aria-modal aria-labelledby={ID_TITULO_DETALLE}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.botonVolver} onClick={onBack} data-testid={TEST_ID_DETALLE_VOLVER}>
            <img src={chevronLeft} alt={CADENA_VACIA} className={styles.iconoVolver} aria-hidden />
            <span id={ID_TITULO_DETALLE}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.detalle.titulo')}</span>
          </button>
        </div>
        {onCerrar ? <BotonCerrarDashboard onCerrar={onCerrar} /> : null}
      </div>

      <div className={styles.cuerpo}>
        <div
          ref={timelineRef}
          className={`${styles.timeline} ${!usa4Etapas && !esProgramado && !esFuturo ? styles.timelineCorto : CADENA_VACIA}`}
          data-testid={timelineTestId}
        >
          {/**
           * Capa de líneas: un tramo por cada par de centros de bolas.
           */}
          <div className={styles.lineasLayer} aria-hidden>
            {lineas.map(seg => (
              <span
                key={seg.indice}
                className={styles.lineaSeg}
                style={
                  {
                    [VARIABLE_LINEA_TOP]: `${seg.top}${UNIDAD_PX}`,
                    [VARIABLE_LINEA_ALTO]: `${seg.height}${UNIDAD_PX}`,
                    [VARIABLE_LINEA_COLOR]: seg.color,
                  } as CSSProperties
                }
              />
            ))}
          </div>

          {pasos.map((paso, index) => {
            const kind = stepKind(index);
            const esUltimo = index === pasos.length - 1;
            const muestraDesc =
              kind === ESTADO_NODO_PENDIENTE
                ? Boolean(paso.mostrarDescSiPendiente)
                : kind === ESTADO_NODO_ACTIVO || Boolean(paso.persistirDescripcion);

            let textoClass = styles.colTexto;
            const muestraHora = kind !== ESTADO_NODO_PENDIENTE && !datoNoValido(paso.hora);
            if (!muestraHora) {
              /**
               * Sin hora se reserva el mismo aire para alinear el título con la bola.
               */
              textoClass = `${textoClass} ${styles.colTextoSinHora}`;
            }
            if (!usa4Etapas && !esProgramado && !esFuturo && !esUltimo) {
              textoClass = `${textoClass} ${styles.colTextoCorto}`;
            }
            if (!esUltimo && !muestraDesc) {
              /**
               * Reserva el espacio de la descripción ausente para que los pasos no se peguen.
               */
              textoClass = `${textoClass} ${styles.colTextoSinCuerpo}`;
            }
            if (esUltimo) {
              textoClass = `${textoClass} ${styles.colTextoUltimo}`;
            }

            const pasoClass = esUltimo ? styles.paso : `${styles.paso} ${styles.pasoConSeparacion}`;

            return (
              <div key={paso.id} className={pasoClass} data-testid={`${PREFIJO_TEST_ID_PASO}${paso.id}`} data-kind={kind}>
                <div className={styles.colIndicador}>
                  {/**
                   * Reserva altura para que el nodo quede a la misma altura relativa en cada paso.
                   */}
                  <span className={styles.horaSpacer} aria-hidden />
                  <div className={styles.nodoSlot}>{renderNodo(paso, kind)}</div>
                </div>
                <div className={textoClass}>{renderTextos(paso, kind)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>{footerTexto}</p>
      </div>
    </div>
  );
};

DetalleAvance.defaultProps = {
  variante: TipoVarianteDetalleEnum.IMPREVISTO,
  comuna: CADENA_VACIA,
  onCerrar: undefined,
};

export default DetalleAvance;
